import { Checkbox, FormGroup } from '@carbon/react';
import type {
  FacetBucket,
  PicklistFilterField,
  FilterTemplateProps,
} from '../../types';
import { bucketLabel } from '../facet-label';
import { useModalFacets, useCategoryFields } from '../template-hooks';
import { PasteListControl } from 'components/paste-list';
import style from '../filter-modal.module.scss';

interface PicklistGroupProps {
  field: PicklistFilterField;
  formValues: Record<string, unknown>;
  facetBuckets: FacetBucket[];
  onToggle: (param: string, id: string) => void;
  onSet: (param: string, ids: string[]) => void;
}

/**
 * One picklist field: a paste box and a checkbox per facet bucket.
 * @param {PicklistGroupProps} props The field.
 * @return {JSX.Element} The field.
 */
function PicklistGroup({
  field,
  formValues,
  facetBuckets,
  onToggle,
  onSet,
}: PicklistGroupProps) {
  const selected = (formValues[field.param] as string[] | undefined) ?? [];

  return (
    <FormGroup legendText={field.label}>
      <PasteListControl
        id={`picklist-${field.id}`}
        label={field.label}
        options={facetBuckets}
        selectedIds={selected}
        onApply={(ids) => onSet(field.param, ids)}
      />
      {facetBuckets.length > 0 ? (
        facetBuckets.map((bucket) => (
          <Checkbox
            key={bucket.id}
            id={`${field.id}-${bucket.id}`}
            labelText={bucketLabel(bucket)}
            checked={selected.includes(bucket.id)}
            onChange={() => onToggle(field.param, bucket.id)}
          />
        ))
      ) : (
        <p className={style.emptyHint}>
          Run a search first to see available options
        </p>
      )}
    </FormGroup>
  );
}

/**
 * A category's picklist fields.
 * @param {FilterTemplateProps} props The template props.
 * @return {JSX.Element} The fields.
 */
export function PicklistTemplate({
  category,
  formValues,
  onFormChange,
}: FilterTemplateProps) {
  const facets = useModalFacets();
  const fields = useCategoryFields(category, 'picklist');

  /**
   * Stages a field's selection, unset when empty.
   * @param {string} param The field's param.
   * @param {string[]} ids The selected ids.
   */
  const handleSet = (param: string, ids: string[]) => {
    onFormChange(param, ids.length > 0 ? ids : undefined);
  };

  /**
   * Adds or removes one id from a field's selection.
   * @param {string} param The field's param.
   * @param {string} id The toggled id.
   */
  const handleToggle = (param: string, id: string) => {
    const current = [...((formValues[param] as string[] | undefined) ?? [])];
    const idx = current.indexOf(id);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(id);
    handleSet(param, current);
  };

  return (
    <div>
      {fields.map((field) => (
        <PicklistGroup
          key={field.id}
          field={field}
          formValues={formValues}
          facetBuckets={facets?.[field.facetKey]?.buckets ?? []}
          onToggle={handleToggle}
          onSet={handleSet}
        />
      ))}
    </div>
  );
}
